import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import { graphql, useStaticQuery } from 'gatsby';

import { TaxonomiesQuery } from '../../generated/types/gatsby';
import { languageFallback } from '../utils/localizations';

type TermData = {
  drupalId: string;
  langcode: string;
  name: string;
  originalName?: string;
};

type Term = {
  drupalId: string;
  langcode: string;
  name: string;
  translations: Array<TermData>;
};

function extractTermsByLocale(
  input: Array<Term>,
  langcode: string,
  fallbackLangcode: string,
): TermData[] {
  const terms = new Map<string, TermData>();
  // To extrat the terms, we will search in all the translations and, if we find
  // a perfect match (meaning the langcode matches and we don't yet have the
  // term in the result map), then we add it to the final result array. If we
  // can't find any translation for an item, we will just try to find one in the
  // fallback langcode.
  input.map((item) => {
    let fallbackItem: TermData | undefined;
    item.translations.map((transltatedItem) => {
      // Check if we have a match, and we did not already add the term to the
      // terms map.
      if (
        transltatedItem.langcode === langcode &&
        !terms.get(transltatedItem.drupalId)
      ) {
        terms.set(transltatedItem.drupalId, {
          ... transltatedItem,
          originalName: item.name
        });
      }
      // Store the fallback item for later use.
      if (transltatedItem.langcode === fallbackLangcode) {
        fallbackItem = {
          ... transltatedItem,
          originalName: item.name
        }
      }
    });
    // If we have a fallback item, and there is no term set in the map, it means
    // we need to use the fallback one.
    if (fallbackItem && !terms.get(fallbackItem.drupalId)) {
      terms.set(fallbackItem.drupalId, fallbackItem);
    }
  });
  return Array.from(terms.values());
}

export const useTaxonomies = (pageContext: SilverbackPageContext) => {
  const defaultLocale = 'de';

  const result = useStaticQuery<TaxonomiesQuery>(graphql`
    query Taxonomies {
      animals: allDrupalAnimal {
        nodes {
          ...AnimalTerm
          translations {
            ...AnimalTerm
          }
        }
      }
      languages: allDrupalLanguage {
        nodes {
          ...LanguageTerm
          translations {
            ...LanguageTerm
          }
        }
      }
      cantons: allDrupalCanton {
        nodes {
          ...CantonTerm
          translations {
            ...CantonTerm
          }
        }
      }
      workTypes: allDrupalWorkType {
        nodes {
          ...WorkTypeTerm
          translations {
            ...WorkTypeTerm
          }
        }
      }
    }
  `);

  const actualLocale = languageFallback(pageContext.locale || defaultLocale);

  return {
    animals: extractTermsByLocale(
      result.animals.nodes || [],
      actualLocale,
      defaultLocale,
    ),
    languages: extractTermsByLocale(
      result.languages.nodes || [],
      actualLocale,
      defaultLocale,
    ),
    cantons: extractTermsByLocale(
      result.cantons.nodes || [],
      actualLocale,
      defaultLocale,
    ),
    workTypes: extractTermsByLocale(
      result.workTypes.nodes || [],
      actualLocale,
      defaultLocale,
    ),
  };
};
